import type { AxiosError, AxiosRequestConfig, CancelTokenSource } from 'axios';
import axios from 'axios';

import type { AssetType } from '../../configs';
import { assetTypes } from '../../configs';
import type { Asset2 } from '../../models';
import campaignGraphqlManager from '../campaignGraphqlManager';
import type {
  CreateAsset2ResponseData,
  CreateAsset2SuccessResponse,
  CreateAssetErrorResponse,
  UploadError,
} from './types';
import { AssetErrorType } from './types';

export function makeFormAssetParams(
  file: File,
  assetType: AssetType,
  creativeLibraryId: string,
  countries: string[]
): FormData {
  const formData: FormData = new FormData();

  formData.append('type', assetType.toLowerCase());
  formData.append('name', file.name);
  formData.append('content', file);
  formData.append('country-codes', countries?.join(',') || '');
  creativeLibraryId && formData.append('creative-library-id', creativeLibraryId);

  return formData;
}

export function makeAsset2ModelFromResponse(data: CreateAsset2ResponseData, type: AssetType): Asset2 {
  return {
    assetTagList: [],
    id: data.id,
    name: '',
    subClass: type,
    review: undefined,
    createdAt: null,
    lastSeenAt: null,
    md5: '',
    s3Url: '',
    assetSources: null,
    countryReviews: [],
    updatedAt: null,
    filename: '',
    countryCodes: [],
    language: null,
  };
}

function makeErrorInfoFromResponse(error: unknown): UploadError {
  if (axios.isCancel(error)) {
    return { type: AssetErrorType.cancel };
  }

  if (!axios.isAxiosError<CreateAssetErrorResponse>(error)) {
    return { type: AssetErrorType.api };
  }

  const details = error.response?.data.error?.details ?? [];
  const message = details[0]?.issue;

  return {
    type: AssetErrorType.api,
    apiStatusCode: error.response?.status,
    message,
  };
}

export interface UploadAsset2MediaFileParams {
  type: AssetType;
  file: File;
  creativeId: string | null;
  creativeLibraryId: string;
  userId: string | null;
  mediaKey: string;
  setPercentUploaded: (percent: number) => void;
  countryCode: string | null;
  cancelTokenSource: CancelTokenSource;
}

export async function uploadAsset2MediaFile({
  type,
  file,
  creativeId,
  creativeLibraryId,
  userId,
  mediaKey,
  setPercentUploaded,
  countryCode,
  cancelTokenSource,
}: UploadAsset2MediaFileParams): Promise<Asset2> {
  const formData = new FormData();

  const ingestionRequest = JSON.stringify({
    'creative-id': creativeId,
    country: countryCode,
    'creative-library-id': creativeLibraryId,
    'user-id': userId,
    'media-key': mediaKey,
  });

  formData.append('ingestion-request', new Blob([ingestionRequest], { type: 'application/json' }));
  formData.append('media-file', file);

  const config: AxiosRequestConfig = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    cancelToken: cancelTokenSource.token,
    onUploadProgress: (event: ProgressEvent) => {
      const percent = Math.round((event.loaded * 100) / event.total);
      setPercentUploaded(percent);
    },
  };

  const uploadEndpoint = type === assetTypes.DisplayAsset.key ? '/api/asset/upload/display' : '/api/asset/upload/video';

  return campaignGraphqlManager
    .post(uploadEndpoint, formData, config)
    .then((response: CreateAsset2SuccessResponse) => makeAsset2ModelFromResponse(response.data, type))
    .catch((error: AxiosError<CreateAssetErrorResponse>) => {
      throw makeErrorInfoFromResponse(error);
    });
}
